import type { components } from '@/generated/contracts/api-v1';
import { fieldMap, requireId, type FieldSpec, type LegacyRow } from './fieldMap';

// ui_images table; ImageDto.From in lureo-backend Data/Api/V1/SceneContent/ImageDtos.cs
export type ImageDto = components['schemas']['ImageDto'];
export type CreateImageRequest = components['schemas']['CreateImageRequest'];

const spec = {
  uniqueId: 'image_id',
  sceneOriginUniqueId: 'scene_origin_id',
  sceneUniqueId: 'scene_instance_id',
  projectUniqueId: 'project_instance_id',
  assetTypeUniqueId: 'asset_type_id',
  userUniqueId: 'user_id',
  category: 'image_category',
  metadata: { legacy: 'image_metadata', as: 'jsonText' },
  originalUrl: 'image_original_url',
  croppedUrl: 'image_cropped_url',
  // Read only: the server makes it from the image (ui_images.thumbnail_url)
  thumbnailUrl: 'thumbnail_url',
  orderNumber: 'image_order_number',
  createdAt: 'created_at',
} as const satisfies FieldSpec<ImageDto>;

const map = fieldMap<ImageDto, typeof spec>('Image', spec);

export type LegacyImage = LegacyRow<typeof spec, ImageDto>;

/** updateImage accepts these. */
export const IMAGE_PATCH_FIELDS = ['category', 'metadata', 'originalUrl', 'croppedUrl', 'orderNumber', 'assetTypeUniqueId'] as const;
const CREATE_FIELDS = ['uniqueId', 'sceneOriginUniqueId', 'sceneUniqueId', 'projectUniqueId', ...IMAGE_PATCH_FIELDS] as const;

export const imageToLegacy = (dto: ImageDto): LegacyImage => map.toLegacy(dto);

/**
 * createImage body from a legacy insert row; image_id is required. user_id is not sent: the
 * uploader is the caller. `replace: true` deletes the slot's other images of the category in the
 * same transaction (what a delete-then-insert did).
 */
export function imageCreateBody(row: Partial<LegacyImage>, options: { replace?: boolean } = {}): CreateImageRequest {
  const body = map.toBody(row, CREATE_FIELDS, ['user_id', 'created_at']);
  return { ...body, uniqueId: requireId('Image', 'image_id', body.uniqueId), replace: options.replace ?? false };
}

/** updateImage body from a legacy partial update. */
export const imagePatchBody = (changes: Partial<LegacyImage>) => map.toBody(changes, IMAGE_PATCH_FIELDS);
